import React, { useEffect, useState } from 'react';
import { Horoscope } from '../../types/horoscope';
import useHoroscopeApi from '../../api/useHoroscopeApi';
import useUserApi from '../../api/useUserApi';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  Divider,
  GridLegacy as Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getHoroscopeTypeText } from '../../services/zodiacService';
import { UserResponse } from '../../types/user';
import dayjs from 'dayjs';

const HoroscopeManagement: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userId = searchParams.get('userId');

  const [horoscopes, setHoroscopes] = useState<Horoscope[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<UserResponse | null>(null);

  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [horoscopeToDelete, setHoroscopeToDelete] = useState<string | null>(null);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedHoroscope, setSelectedHoroscope] = useState<Horoscope | null>(null);

  const { getUserHoroscopes, deleteHoroscope } = useHoroscopeApi();
  const { getUser } = useUserApi();

  useEffect(() => {
    fetchHoroscopes();
    fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      const user = await getUser(userId || '');
      setUserDetails(user);
    } catch (err) {
      console.error('Failed to fetch user details:', err);
    }
  };

  const fetchHoroscopes = async () => {
    try {
      setLoading(true);
      const data = await getUserHoroscopes(userId || '');
      setHoroscopes(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch horoscopes:', err);
      setError('נכשל בטעינת ההורוסקופים');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (id: string) => {
    setHoroscopeToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setHoroscopeToDelete(null);
  };

  const handleDelete = async () => {
    if (horoscopeToDelete) {
      try {
        await deleteHoroscope(horoscopeToDelete);
        fetchHoroscopes();
        closeDeleteDialog();
      } catch (err) {
        console.error('Failed to delete horoscope:', err);
        setError('נכשל במחיקת ההורוסקופ');
        closeDeleteDialog();
      }
    }
  };

  const handleRowClick = (horoscope: Horoscope) => {
    setSelectedHoroscope(horoscope);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedHoroscope(null);
  };

  const handleBack = () => {
    navigate('/users');
  };

  if (loading && horoscopes.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={3} sx={{ p: 3, position: 'relative', mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'center', mb: 3 }}>
          <Button startIcon={<ArrowBackIcon sx={{ marginLeft: '12px' }} />} onClick={handleBack} variant="outlined"
          >
            חזרה למשתמשים
          </Button>
        </Box>

        {userDetails && (
          <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">
                  {userDetails.first_name} {userDetails.last_name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  אימייל: {userDetails.email}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  טלפון: {userDetails.mobile}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">תאריך לידה:
                  {dayjs(userDetails.birth_date).format('DD/MM/YYYY')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  זמן לידה:
                  {userDetails.birth_time ? dayjs(userDetails.birth_time).format('HH:mm') : 'לא צוין'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        <TableContainer>
          <Table dir="rtl">
            <TableHead>
              <TableRow>
                <TableCell align="right">סוג</TableCell>
                <TableCell align="right">תאריך יצירה</TableCell>
                <TableCell align="right">תחומי עניין</TableCell>
                <TableCell align="right">תוכן</TableCell>
                <TableCell align="right">פעולות</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {horoscopes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    לא נמצאו הורוסקופים.
                  </TableCell>
                </TableRow>
              ) : (
                horoscopes.map((horoscope) => (
                  <TableRow
                    key={horoscope._id}
                    onClick={() => handleRowClick(horoscope)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                  >
                    <TableCell align="right">{getHoroscopeTypeText(horoscope.horoscope_type)}</TableCell>
                    <TableCell align="right">
                      <Typography>{dayjs(horoscope.created_at).format('DD/MM/YYYY HH:mm')}</Typography>
                    </TableCell>
                    <TableCell align="right">{horoscope.interests}</TableCell>
                    <TableCell dir="rtl" align="right">
                      {horoscope.horoscope_response.length > 50
                        ? `${horoscope.horoscope_response.substring(0, 50)}...`
                        : horoscope.horoscope_response}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteDialog(horoscope._id);
                          }}
                        >
                          מחיקה
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Horoscope Details Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: '40%',
            minWidth: '300px',
            maxWidth: '600px',
            p: 3
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">פרטי הורוסקופ</Typography>
          <IconButton onClick={closeDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {selectedHoroscope && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">סוג</Typography>
              <Typography>{getHoroscopeTypeText(selectedHoroscope.horoscope_type)}</Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">תאריך יצירה</Typography>
              <Typography>{dayjs(selectedHoroscope.created_at).format('DD/MM/YYYY HH:mm')}</Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">תחומי עניין</Typography>
              <Typography>{selectedHoroscope.interests}</Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">תוכן</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selectedHoroscope.horoscope_response}</Typography>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Confirmation Dialog for Delete */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        dir="rtl"
        PaperProps={{
          sx: {
            maxWidth: '500px',
            width: '100%'
          }
        }}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">אישור מחיקה</DialogTitle>
        <DialogContent dividers>
          <DialogContentText id="delete-dialog-description" dir="rtl">
            האם אתה בטוח שברצונך למחוק הורוסקופ זה? פעולה זו אינה ניתנת לביטול.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', direction: 'rtl', p: 2 }}>
          <Button onClick={handleDelete} color="error" variant="contained">
            מחיקה
          </Button>
          <Button onClick={closeDeleteDialog} color="primary">
            ביטול
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HoroscopeManagement; 